/**
 * Nav rail / drawer / user menu patterns — plain objects for inline `style={}`.
 * Covers desktop rail header, icon cells, toggle button, brand row, and user account menu.
 */

import { foundation as f } from './tokens';
import { legacy } from './theme/semanticTokens';

const { color, spacePx, radii, fontSize, fontWeight, fontFamily } = f;

// ─── Desktop Rail ───────────────────────────────────────────────────────────

/** Mobile drawer icon column — matches NavigationNavItems mobile rows */
export const mobileNavIconTile = {
  width: '24px',
  height: '24px',
  minWidth: '24px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

/** Mobile drawer shell + row padding — keep nav items and account footer in sync */
export const MOBILE_NAV_LIST_PADDING = `${spacePx[2]} 10px`;

export const mobileNavRowStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: spacePx[3],
  minHeight: '44px',
  boxSizing: 'border-box',
  padding: `${spacePx[1]} ${spacePx[3]}`,
};

/** Desktop account footer — same flex row as navDrawerItem buttons */
export const desktopNavAccountRowStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  height: '44px',
  minHeight: '44px',
  boxSizing: 'border-box',
  padding: 0,
  margin: 0,
  gap: 0,
};

/** Desktop account footer shell — inset from rail bottom so profile isn't flush */
export const desktopNavAccountFooterStyle = {
  flexShrink: 0,
  borderTop: `1px solid ${color.neutral.border}`,
  paddingTop: spacePx[2],
  paddingBottom: spacePx[3],
};

export const NAV_AVATAR_DESKTOP_PX = 28;
export const NAV_AVATAR_MOBILE_PX = 24;

/** @deprecated Use NAV_AVATAR_DESKTOP_PX / NAV_AVATAR_MOBILE_PX */
export const NAV_AVATAR_PX = NAV_AVATAR_DESKTOP_PX;

/** Footer avatar circle — centered inside mobileNavIconTile */
export function navAvatarBubbleStyle(userAv, sizePx = NAV_AVATAR_PX) {
  return {
    width: `${sizePx}px`,
    height: `${sizePx}px`,
    borderRadius: '50%',
    backgroundColor: userAv.bg,
    color: userAv.text,
    border: userAv.border ? `1px solid ${userAv.border}` : undefined,
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: fontFamily.sans,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semibold,
    flexShrink: 0,
    lineHeight: 1,
  };
}

export function navRailPeekLabelStyle({ reduceMotion = false } = {}) {
  // ONE simple tooltip for the collapsed rail (founder, 2026-10-13): a small dark chip
  // to the right of the icon — same look for every item (active included) and the
  // expand toggle. It only exists while the rail is collapsed; open rails show names.
  return {
    position: 'absolute',
    left: '100%',
    top: '50%',
    transform: 'translateY(-50%)',
    marginLeft: 10,
    zIndex: 2,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    padding: '6px 10px',
    borderRadius: 8,
    fontFamily: fontFamily.sans,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    lineHeight: 1.2,
    color: color.neutral.textInverse,
    backgroundColor: color.neutral.inverse,
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.18)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    ...(reduceMotion
      ? { opacity: 1 }
      : {
          opacity: 0,
          animation: 'cvxNavRailPeekIn 120ms cubic-bezier(0.33, 1, 0.68, 1) forwards',
        }),
  };
}

/** Keyboard-shortcut hint inside the rail tooltip ("[") — plain muted text, no box. */
export const navRailTooltipKeyHint = {
  fontWeight: fontWeight.semibold,
  opacity: 0.55,
};

/** Expand/collapse toggle button — consistent size in both states */
export const railMenuToggle = {
  appearance: 'none',
  WebkitAppearance: 'none',
  width: '32px',
  height: '32px',
  minWidth: '32px',
  padding: 0,
  margin: 0,
  border: `1px solid ${color.neutral.border}`,
  borderRadius: radii.md,
  backgroundColor: color.neutral.bgSubtle,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: color.neutral.textSecondary,
  boxSizing: 'border-box',
  flexShrink: 0,
};

/** Brand row layout inside rail — flex row, vertically centered */
export const railBrandRow = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  minHeight: '44px',
};

/** Invisible clickable brand area (no visual chrome) */
export const railBrandButton = {
  display: 'inline-flex',
  alignItems: 'center',
  minWidth: 0,
  flex: 1,
  padding: 0,
  margin: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: 'inherit',
  font: 'inherit',
  textAlign: 'left',
  justifyContent: 'flex-start',
};

/** Alignment spacer column matching nav icon width so labels align with product name */
export const railBrandSpacer = {
  width: '44px',
  minWidth: '44px',
  height: '44px',
  flexShrink: 0,
};

// ─── User Menu ───────────────────────────────────────────────────────────────

/** Relative-position wrapper for the user account dropdown */
export const userMenuContainer = {
  position: 'relative',
};

/** Pill-shaped account header trigger button */
export const userMenuTrigger = {
  fontFamily: fontFamily.sans,
  display: 'inline-flex',
  alignItems: 'center',
  gap: spacePx[2],
  padding: `${spacePx[2]} ${spacePx[2]}`,
  borderRadius: '999px',
  backgroundColor: color.neutral.surface,
  border: `1px solid ${legacy.border}`,
  color: color.neutral.textSecondary,
  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
  cursor: 'pointer',
};

/** Circular avatar bubble inside the user menu trigger */
export const userIconBubble = {
  width: '28px',
  height: '28px',
  borderRadius: '999px',
  backgroundColor: legacy.menuTint,
  border: `1px solid ${legacy.border}`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.8rem',
  flexShrink: 0,
};

/** Absolute-positioned dropdown panel */
export const menuDropdownPanel = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  minWidth: '190px',
  borderRadius: radii.lg,
  border: `1px solid ${legacy.border}`,
  backgroundColor: color.neutral.surfaceElevated,
  boxShadow: '0 14px 30px rgba(15, 23, 42, 0.12)',
  padding: spacePx[1],
  zIndex: 120,
};

/** Full-width menu item button */
export const menuItem = {
  fontFamily: fontFamily.sans,
  width: '100%',
  textAlign: 'left',
  padding: `9px ${spacePx[2]}`,
  borderRadius: radii.md,
  border: '1px solid transparent',
  backgroundColor: 'transparent',
  color: color.neutral.textSecondary,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.medium,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacePx[2],
};

/** Icon + label row inside a menu item */
export const menuItemContent = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: spacePx[2],
};

/** Circular icon badge inside a menu item */
export const menuItemIcon = {
  width: '20px',
  height: '20px',
  borderRadius: '999px',
  backgroundColor: legacy.menuTint,
  border: `1px solid ${legacy.border}`,
  color: color.neutral.textSecondary,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.72rem',
  lineHeight: 1,
  flexShrink: 0,
};

/** Hover surface for a menu item */
export const menuItemHover = {
  backgroundColor: color.neutral.bgSubtle,
  borderColor: legacy.border,
};

/** Danger text for destructive menu items */
export const menuItemDanger = {
  color: color.semantic.danger,
};

/** Danger hover surface for destructive menu items */
export const menuItemDangerHover = {
  backgroundColor: color.semantic.dangerSoft,
  borderColor: color.semantic.dangerBorder,
};

/**
 * Canonical tone-aware menu item style. Single source of truth for action
 * dropdowns (estimate row actions, invoice preview overflow, future report
 * actions). Tones: 'default' | 'warning' | 'success' | 'danger'.
 */
export function getMenuItemStyle(tone, hovered) {
  const base = {
    ...menuItem,
    justifyContent: 'flex-start',
    fontWeight: fontWeight.semibold,
    color: color.neutral.text,
    appearance: 'none',
    WebkitAppearance: 'none',
    outline: 'none',
    border: '1px solid transparent',
    transition: 'background-color 120ms ease, color 120ms ease',
  };
  if (tone === 'danger')  return { ...base, color: color.semantic.danger,  backgroundColor: hovered ? color.semantic.dangerSoft  : 'transparent' };
  if (tone === 'success') return { ...base, color: color.semantic.success, backgroundColor: hovered ? color.semantic.successSoft : 'transparent' };
  if (tone === 'warning') return { ...base, color: color.semantic.warning, backgroundColor: hovered ? color.semantic.warningSoft : 'transparent' };
  // 'info' — the blue the Sent status pill uses (dataSurfaceStyles); for
  // actions whose outcome IS the sent state (e.g. mark-as-sent external).
  if (tone === 'info')    return { ...base, color: color.semantic.info,    backgroundColor: hovered ? color.semantic.infoSoft    : 'transparent' };
  return { ...base, backgroundColor: hovered ? color.neutral.bgSubtle : 'transparent' };
}
